import {
  Dialog,
  FormInput,
  FormSectionHeader,
  FormSelect,
  FormTextarea,
  Heading,
  Loader,
} from "@/components";
import useAddEditBudget from "./useAddEditBudget";
import { FormProvider } from "react-hook-form";
import {
  BUDGET_ID,
  BUDGET_NAME,
  LAST_UPDATED_BY,
  LAST_UPDATED_ON,
  NOTES,
  TOTAL_ANNUAL_BUDGET,
  VERSION_NO,
  YEAR,
} from "./consts";
import { Button } from "@/components/ui";
import { yearOptions } from "@/lib/utils";
import moment from "moment";

const AddEditBudget = () => {
  const {
    breadcrumbData,
    onBackClick,
    isView,
    onSubmit,
    form,
    requiredFields,
    onCancel,
    isAddPending,
    isUpdatePending,
    isBudgetDataLoading,
    budgetId,
    yearKey,
    commentsData,
    isCommentsLoading,
    openApprovalDialog,
    setOpenApprovalDialog,
    isSubmitApprovalPending,
    onSubmitApproval,
    onSubmitApprovalBtnClick,
    versionKey,
    formData,
    isAdd,
    isArchived,
  } = useAddEditBudget();
  const { errors } = form.formState;

  return (
    <div className="p-4 md:p-6 !pt-0">
      <div className="flex flex-col lg:flex-row lg:items-center w-full justify-between">
        <Heading
          title={breadcrumbData[breadcrumbData.length - 1].name}
          breadcrumbData={breadcrumbData}
          showBack
          onBackClick={onBackClick}
        />
      </div>

      {!isAdd && isArchived && <div className="text-stone-600 bg-amber-50 border-yellow-400 border-1 p-5 text-[12px] rounded-[10px] mt-[20px]">
        <span className="font-semibold">Financial Year - {formData[YEAR]}</span>

        <div className="italic my-3">
          <span className="font-semibold">This budget is a revision of an already approved budget.</span>
          <ul className="list-disc pl-4">
            <li>Financial posting is currently blocked.</li>
            <li>Any budget line item with posted financial documents cannot be deleted.</li>
            <li>You may edit existing GL lines or add new GL lines.</li>
          </ul>
        </div>
        <span className="font-semibold">Submit the budget for BM approval once revisions are complete.</span>
      </div>}

      {isBudgetDataLoading || isCommentsLoading ? (
        <Loader />
      ) : (
        <>
          <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className="bg-white mt-[20px] rounded-lg px-[30px] py-[25px] grid grid-cols-1 gap-[30px]">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[30px]">
                  {budgetId && (
                    <FormInput
                      name={BUDGET_ID}
                      control={form.control}
                      label="Budget ID"
                      placeholder="Please enter"
                      error={errors[BUDGET_ID]?.message}
                      isRequired={requiredFields.includes(BUDGET_ID)}
                      disabled
                    />
                  )}
                  {!budgetId && (
                    <FormInput
                      name={BUDGET_NAME}
                      control={form.control}
                      label="Budget Name"
                      placeholder="Please enter"
                      error={errors[BUDGET_NAME]?.message}
                      isRequired={requiredFields.includes(BUDGET_NAME)}
                      disabled={isView}
                    />
                  )}
                  <FormSelect
                    key={yearKey}
                    name={YEAR}
                    control={form.control}
                    label="Year"
                    placeholder="Please select"
                    error={errors[YEAR]?.message}
                    isRequired={requiredFields.includes(YEAR)}
                    disabled={isView || !!budgetId}
                    options={yearOptions(5)}
                  />
                  <FormSelect
                    key={versionKey}
                    name={VERSION_NO}
                    control={form.control}
                    label="Version No"
                    placeholder="Please enter"
                    error={errors[VERSION_NO]?.message}
                    isRequired={requiredFields.includes(VERSION_NO)}
                    disabled={isView || !!budgetId}
                    options={new Array(26).fill(0).map((_, index) => {
                      return {
                        label: index?.toString(),
                        value: index.toString(),
                      };
                    })}
                  />
                  {budgetId && (
                    <>
                      <FormInput
                        name={TOTAL_ANNUAL_BUDGET}
                        control={form.control}
                        label="Total Annual Budget"
                        placeholder="Please enter"
                        error={errors[TOTAL_ANNUAL_BUDGET]?.message}
                        isRequired={requiredFields.includes(
                          TOTAL_ANNUAL_BUDGET
                        )}
                        disabled
                        variant="integer"
                        startIcon={<span>$</span>}
                      />
                      <FormInput
                        name={LAST_UPDATED_BY}
                        control={form.control}
                        label="Last Updated By"
                        placeholder="Please enter"
                        error={errors[LAST_UPDATED_BY]?.message}
                        isRequired={requiredFields.includes(LAST_UPDATED_BY)}
                        disabled
                      />
                      <FormInput
                        name={LAST_UPDATED_ON}
                        control={form.control}
                        label="Last Updated On"
                        placeholder="Please enter"
                        error={errors[LAST_UPDATED_ON]?.message}
                        isRequired={requiredFields.includes(LAST_UPDATED_ON)}
                        disabled
                      />
                    </>
                  )}
                </div>
                <FormTextarea
                  name={NOTES}
                  control={form.control}
                  label="Notes"
                  placeholder="Please enter"
                  error={errors[NOTES]?.message}
                  isRequired={requiredFields.includes(NOTES)}
                  disabled={isView}
                />
              </div>
              <div className="flex flex-row justify-end gap-3  py-3">
                <Button
                  variant="outline"
                  type="button"
                  onClick={onCancel}
                  disabled={isAddPending || isUpdatePending}
                >
                  Cancel
                </Button>
                {form.watch(VERSION_NO) == "0" &&
                  !isView &&
                  !!budgetId && (
                  <Button
                    variant="outline"
                    type="button"
                    onClick={onSubmitApprovalBtnClick}
                    disabled={isAddPending || isUpdatePending}
                  >
                    Submit For Approval
                  </Button>
                )}
                {!isView && (
                  <Button
                    type="submit"
                    disabled={isAddPending || isUpdatePending}
                  >
                    {isAddPending ||
                      (isUpdatePending && <Loader variant="secondary" />)}
                    Save
                  </Button>
                )}
              </div>
            </form>
          </FormProvider>

          {budgetId && (
            <div className="bg-white mt-[20px] rounded-lg px-[30px] py-[25px]">
              <FormSectionHeader className="text-[20px]">
                Comments
              </FormSectionHeader>
              <div className="max-h-[150px] overflow-y-auto grid grid-cols-1 gap-[20px]">
                {commentsData?.data
                  ?.sort(
                    (a: any, b: any) =>
                      new Date(b.createdAt).getTime() -
                      new Date(a.createdAt).getTime()
                  )
                  ?.map((data: any) => {
                    return (
                      <div
                        key={data.commentId}
                        className="text-sm flex flex-col gap-1"
                      >
                        <div className="text-primary font-[500]">
                          {data?.createdByName}: (
                          {moment(data?.createdAt).format("MM/DD/YYYY hh:mm A")}
                          )
                        </div>
                        <div className="font-[400]">{data?.comment}</div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
        </>
      )}

      {/* Approval Dialog */}
      {openApprovalDialog && (
        <Dialog
          open={openApprovalDialog}
          setOpen={setOpenApprovalDialog}
          title={
            <div className="text-2xl font-bold text-primary">
              {"Confirmation"}
              <div className="h-1 w-[30%] bg-primary rounded-full" />
            </div>
          }
          saveText="Yes"
          cancelText="No"
          isLoading={isSubmitApprovalPending}
          onSave={onSubmitApproval}
        >
          <p className="text-sm">
            You are about to submit the budget for approval. Once submitted, the
            budget will move to{" "}
            <b className="text-primary">'Pending for Approval'</b> status and
            cannot be edited until the approval process is completed. Do you
            want to proceed?
          </p>
        </Dialog>
      )}
    </div>
  );
};

export default AddEditBudget;
